import { openDB, DBSchema } from 'idb';
import { StateStorage } from 'zustand/middleware';

const dbName = 'log-database';
const storeName = 'log-lead-temp';

const openDBLog = openDB(dbName, 1, {
    upgrade(db) {
        db.createObjectStore(storeName);
    }
})

const get = async (key: string): Promise<any | null> => {
    try {
        const result = await (await openDBLog).get(storeName, key);
        return result;
    } catch (error) {
        console.error(`Error getting item with key "${key}":`, error);
        return null;
    }
};

const set = async (key: string, value: any): Promise<IDBValidKey | null> => {
    try {
        const result = await (await openDBLog).put(storeName, value, key);
        return result;
    } catch (error) {
        console.error(`Error setting item with key "${key}":`, error);
        return null;
    }
}

const del = async (key: string): Promise<void> => {
    try {
        await (await openDBLog).delete(storeName, key);
    } catch (error) {
        console.error(`Error deleting item with key "${key}":`, error);
    }
}

const getAll = async (): Promise<any[]> => {
    try {
        const result = await (await openDBLog).getAll(storeName);
        return result;
    } catch (error) {
        console.error('Error getting all items:', error);
        return [];
    }
}

const getAllKeys = async (): Promise<IDBValidKey[]> => {
    try {
        const result = await (await openDBLog).getAllKeys(storeName);
        return result;
    } catch (error) {
        console.error('Error getting all keys:', error);
        return [];
    }
}

const count = async (): Promise<number> => {
    try {
        const result = await (await openDBLog).count(storeName);
        return result;
    } catch (error) {
        console.error('Error counting items:', error);
        return 0;
    }
}

const clear = async (): Promise<void> => {
    try {
        await (await openDBLog).clear(storeName);
    } catch (error) {
        console.error('Error clearing store:', error);
    }
}

const add = async (value: any, key?: string): Promise<IDBValidKey | null> => {
    try {
        const result = await (await openDBLog).add(storeName, value, key);
        return result;
    } catch (error) {
        console.error('Error adding item:', error);
        return null;
    }
}

const bulkUpdate = async (items: { key: string, value: any }[]): Promise<void> => {
    try {
        const db = await openDBLog;
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);

        for (const item of items) {
            await store.put(item.value, item.key);
        }

        await tx.done;
    } catch (error) {
        console.error('Error in bulk update:', error);
    }
}

const bulkDelete = async (keys: string[]): Promise<void> => {
    try {
        const db = await openDBLog;
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);

        for (const key of keys) {
            await store.delete(key);
        }

        await tx.done;
    } catch (error) {
        console.error('Error in bulk delete:', error);
    }
}

const objectStorage = {
    getItem: async (name: string): Promise<any | null> => {
        return await get(name);
    },
    setItem: async (name: string, value: any): Promise<void> => {
        await set(name, value);
    },
    removeItem: async (name: string): Promise<void> => {
        await del(name);
    },

    // Extended methods
    getAll: async (): Promise<any[]> => {
        return await getAll();
    },
    getAllKeys: async (): Promise<IDBValidKey[]> => {
        return await getAllKeys();
    },
    count: async (): Promise<number> => {
        return await count();
    },
    clear: async (): Promise<void> => {
        await clear();
    },
    add: async (value: any, key?: string): Promise<IDBValidKey | null> => {
        return await add(value, key);
    },

    // Bulk operations
    bulkUpdate: async (items: { key: string, value: any }[]): Promise<void> => {
        await bulkUpdate(items);
    },
    bulkDelete: async (keys: string[]): Promise<void> => {
        await bulkDelete(keys);
    }
}

const storage: StateStorage = {
    getItem: async (name: string): Promise<string | null> => {
        const result = await get(name);
        return typeof result === 'string' ? result : JSON.stringify(result);
    },
    setItem: async (name: string, value: string): Promise<void> => {
        await set(name, value);
    },
    removeItem: async (name: string): Promise<void> => {
        await del(name);
    },
}

export { storage, objectStorage };